"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FundNavSeries } from "@/type/comparePageTypes";
import { calculateReturnsForChart, getCutoffDate, normalizeNavMap, rangeFromExtremes, toTime, updateSeriesVisibility } from "@/utils/comparePageFunctions";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import Icon from "@/components/icons/Icons";
import Image from "next/image";
import CustomDropdown from "@/components/ui/CustomDropdown";
import CustomCalendar from "@/components/ui/CustomCalendar";
import { format } from "date-fns";

type RangeKey = "1M" | "3M" | "6M" | "YTD" | "1Y" | "3Y" | "5Y" | "ALL";

Highcharts.addEvent(Highcharts.Series, "afterRender", function () {
  const series = this as Highcharts.Series;
  const legendItem = series.legendItem;

  if (!legendItem) return;

  const symbol = legendItem.symbol;
  const color = series.color;

  if (!symbol || !color) return;

  symbol.attr({
    fill: color,
    stroke: color,
  });
});

export default function CompareFundsNavChart({ funds }: { funds: FundNavSeries[] }) {
  const [range, setRange] = useState<RangeKey>("5Y");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);
  const [returns, setReturns] = useState<Record<string, number>>({});
  const [isChartLoading, setIsChartLoading] = useState(true);

  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const startBtnRef = useRef<HTMLButtonElement>(null);
  const endBtnRef = useRef<HTMLButtonElement>(null);
  const rangeWrapperRef = useRef<HTMLDivElement>(null);
  const rangeBtnRefs = useRef<Record<RangeKey, HTMLButtonElement | null>>({
    "1M": null,
    "3M": null,
    "6M": null,
    YTD: null,
    "1Y": null,
    "3Y": null,
    "5Y": null,
    ALL: null,
  });

  useEffect(() => {
    const wrapper = rangeWrapperRef.current;
    const btn = rangeBtnRefs.current[range];

    if (!wrapper || !btn) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    const offset = btnRect.left - wrapperRect.left - wrapper.clientWidth / 2 + btn.clientWidth / 2;

    wrapper.scrollTo({
      left: wrapper.scrollLeft + offset,
      behavior: "auto", // important: no smooth on load
    });
  }, [range]);

  // Applied the Range of the Date fields
  function applyDateRange(from: Date | null, to: Date | null) {
    const chart = chartRef.current?.chart;
    if (!chart || !from || !to) return;

    chart.xAxis[0].setExtremes(from.getTime(), to.getTime(), true, false);
  }

  // For Syncronized the current range to the buttons and dates fields
  function getSeriesDateExtremes(): { min: number; max: number } | null {
    const allDates = funds.flatMap((fund) => Object.values(fund.data)).map((d) => new Date(d.date).getTime());

    if (!allDates.length) return null;

    return {
      min: Math.min(...allDates),
      max: Math.max(...allDates),
    };
  }

  // For Syncronized the current range to the buttons and dates fields
  function getExtremesForRange(key: RangeKey) {
    const extremes = getSeriesDateExtremes();
    if (!extremes) return { min: undefined, max: undefined };

    const { min: chartMin, max: chartMax } = extremes;

    const latestDate = new Date(chartMax);
    const cutoff = getCutoffDate(latestDate, key);

    return { min: cutoff?.getTime() ?? chartMin, max: chartMax };
  }

  const extremes = useMemo(() => {
    const allDates = funds.flatMap((fund) => Object.values(fund.data).map((d) => new Date(d.date).getTime()));

    if (!allDates.length) {
      return { min: undefined, max: undefined };
    }

    return {
      min: Math.min(...allDates), // oldest date across all series
      max: new Date().getTime(), // today
    };
  }, [funds]);

  // setting the series
  const series = useMemo<Highcharts.SeriesLineOptions[]>(() => {
    return funds.map((fund): Highcharts.SeriesLineOptions => {
      const navData = normalizeNavMap(fund.data);

      const sorted = [...navData].sort((a, b) => toTime(a.date) - toTime(b.date));

      const latest = sorted[sorted.length - 1];

      if (!latest) {
        return {
          type: "line",
          name: fund.name,
          color: fund.color,
          data: [],
        };
      }

      return {
        type: "line",
        name: fund.name,
        color: fund.color,
        compare: "percent",
        lineWidth: 2,
        marker: { enabled: false },
        data: sorted.map((d) => [toTime(d.date), d.nav]),
      };
    });
  }, [funds, range]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    requestAnimationFrame(() => {
      updateSeriesVisibility(chart);
      calculateReturnsForChart(chart, setReturns);
    });
  }, [series]);

  // Highcharts function
  const options: Highcharts.Options = {
    chart: {
      height: 440,
      backgroundColor: "transparent",
      events: {
        load: function (this: Highcharts.Chart) {
          const { min, max } = getExtremesForRange("5Y");

          if (typeof min !== "number" || typeof max !== "number") return;

          this.xAxis[0].setExtremes(min, max, true, false);
          calculateReturnsForChart(this, setReturns);
          // updateSeriesVisibility(this);
          setIsChartLoading(false);
        },
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "datetime",
      tickLength: 0,
      lineColor: "#E5E7EB",
      gridLineWidth: 1,
      gridLineDashStyle: "Dash",
      gridLineColor: "#E5E7EB",
      labels: {
        style: { color: "#6B7280", fontSize: "12px" },
      },
      events: {
        afterSetExtremes: function (e) {
          const chart = this.chart;
          if (typeof e.min !== "number" || typeof e.max !== "number") return;

          const r = rangeFromExtremes(e.min, e.max);
          
          setDateFrom(new Date(e.min));
          setDateTo(new Date(e.max));
          
          // updateSeriesVisibility(chart);
          calculateReturnsForChart(chart, setReturns);
          setRange(r);
        },
      },
    },
    yAxis: {
      title: { text: "" },
      gridLineDashStyle: "Dash",
      gridLineColor: "#E5E7EB",
      labels: {
        enabled: false,
      },
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
      width: "100%",
      itemWidth: 280,
      symbolRadius: 8,
      itemMarginBottom: 8,
      itemStyle: {
        fontSize: "14px",
        fontWeight: "500",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        opacity: 1,
      },
      labelFormatter: function () {
        const value = returns[this.name] ?? 0;
        const sign = value >= 0 ? "+" : "";

        return `<div style="color:${this.color}">
                  <span class="line-clamp-1 max-w-36"> ${this.name}</span> <br/>
                  <span>(${sign} ${value.toFixed(2)}%)</span> 
                </div>`;
      },
      navigation: {
        enabled: false,
      },
      itemHiddenStyle: {
        textDecoration: "none",
        opacity: 0.5,
      },
    },
    plotOptions: {
      series: {
        animation: true,
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
      },
    },
    tooltip: {
      shared: true,
      valueDecimals: 2,
      pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y}</b> ({point.change}%)<br/>',
    },
    series,
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 768,
          },
          chartOptions: {
            chart: {
              height: 420,
            },
            legend: {
              itemWidth: 220,
              itemStyle: {
                fontSize: "12px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "11px",
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 431,
          },
          chartOptions: {
            legend: {
              layout: "vertical",
              align: "center",
              itemWidth: undefined,
              itemStyle: {
                fontSize: "11px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "10px",
                },
              },
            },
            plotOptions: {
              series: {
                lineWidth: 2,
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div className="w-full rounded-3xl border border-gray-200 bg-white xs:p-2.5">
      {isChartLoading && (
        <div className="flex items-center justify-center h-105 sm:h-110">
          <Image src="/next_assets/images/gif/chart_loading.gif" unoptimized alt="Loading Chart Animation" loading="eager" width={256} height={144} priority className="w-64 h-36 mx-auto" />
        </div>
      )}

      <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="stockChart" options={options} />

      <div className="2md:flex items-center justify-between space-y-5 2md:space-y-0 gap-5 p-4 sm:px-6">
        <div ref={rangeWrapperRef} className="xs:w-fit mx-auto 2md:mx-0 flex items-center gap-1 xs:gap-6 2md:gap-3 overflow-x-auto scrollbar-none bg-[#F5F9FF] p-0.5 border border-[#E4E4E4] rounded-lg xs:bg-transparent xs:p-0 xs:border-0 xs:rounded-none">
          {(["1M", "3M", "6M", "YTD", "1Y", "3Y", "5Y", "ALL"] as RangeKey[]).map((key) => (
            <button
              key={key}
              ref={(el) => {
                rangeBtnRefs.current[key] = el;
              }}
              onClick={() => {
                const chart = chartRef.current?.chart;
                if (!chart) return;
                const { min, max } = getExtremesForRange(key);
                
                chart.xAxis[0].setExtremes(min, max, true, false);
                setRange(key);
                
              }}
              className={`px-3 sm:px-4 py-1.5 rounded-md xs:rounded-full text-2xs sm:text-2sm xs:border transition ${range === key ? "bg-blue-600 text-white border-blue-600" : "xs:bg-white text-gray-600 border-gray-300 xs:hover:border-blue-400"}`}
            >
              {key}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-center gap-1.5 sm:gap-3 relative">
          <div>
            <button ref={startBtnRef} onClick={() => setIsStartCalendarOpen((p) => !p)} className={`w-full gap-4 sm:gap-5 py-1.5 px-1.5 sm:py-1.5 sm:px-3 rounded-md sm:rounded-lg text-left bg-white border text-2xs sm:text-sm sm:leading-5 font-medium leading-1 flex items-center justify-between transition duration-300 ${isStartCalendarOpen ? "text-primaryBlue border-primaryBlue" : "text-[#5f5f5f] border-[#aaaaaa] hover:text-black/70 hover:border-black/70"}`}>
              {dateFrom ? format(dateFrom, "dd MMM yyyy") : "From"}
              <Icon.dropdownDownArrow className={`fill-primaryBlack transition ${isStartCalendarOpen ? "rotate-180" : ""}`} />
            </button>

            <CustomDropdown isOpen={isStartCalendarOpen} onClose={() => setIsStartCalendarOpen(false)} className="absolute bottom-full right-0 w-full mb-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-1.5 overflow-hidden max-w-80" excludeRefs={[startBtnRef]}>
              <CustomCalendar
                defaultSelectedDate={dateFrom ?? undefined}
                minSelectableDate={new Date(extremes.min ?? 0)} // oldest date
                maxSelectableDate={dateTo ? new Date(dateTo.getTime() - 24 * 60 * 60 * 1000) : new Date(extremes.max ?? Date.now())}
                onDateSelect={(d) => {
                  setDateFrom(d);
                  applyDateRange(d, dateTo);
                  setIsStartCalendarOpen(false);
                }}
              />
            </CustomDropdown>
          </div>

          <Icon.rightArrow className="shrink-0 text-[#444444]" />

          <div>
            <button ref={endBtnRef} onClick={() => setIsEndCalendarOpen((p) => !p)} className={`w-full gap-4 sm:gap-5 py-1.5 px-1.5 sm:py-1.5 sm:px-3 rounded-md sm:rounded-lg text-left bg-white border text-2xs sm:text-sm sm:leading-5 font-medium leading-1 flex items-center justify-between transition duration-300 ${isEndCalendarOpen ? "text-primaryBlue border-primaryBlue" : "text-[#5f5f5f] border-[#aaaaaa] hover:text-black/70 hover:border-black/70"}`}>
              {dateTo ? format(dateTo, "dd MMM yyyy") : "To"}
              <Icon.dropdownDownArrow className={`fill-primaryBlack transition ${isEndCalendarOpen ? "rotate-180" : ""}`} />
            </button>

            <CustomDropdown isOpen={isEndCalendarOpen} onClose={() => setIsEndCalendarOpen(false)} className="absolute bottom-full right-0 w-full mb-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-1.5 overflow-hidden max-w-80" excludeRefs={[endBtnRef]}>
              <CustomCalendar
                defaultSelectedDate={dateTo ?? undefined}
                minSelectableDate={dateFrom ? new Date(dateFrom.getTime() + 24 * 60 * 60 * 1000) : new Date(extremes.min ?? 0)} // after "from"
                maxSelectableDate={new Date(extremes.max ?? Date.now())} // today
                onDateSelect={(d) => {
                  setDateTo(d);
                  applyDateRange(dateFrom, d);
                  setIsEndCalendarOpen(false);
                }}
              />
            </CustomDropdown>
          </div>
        </div>
      </div>
    </div>
  );
}
